// Cards y heroes del browse de la home V2. Dos componentes
// presentacionales:
//
//   • MobileModeHero — carousel swipeable mobile-only que reemplaza
//     la fila de cards "Diseña tu imagen" en mobile. Slides verticales
//     (aspect 2:3) con video/imagen + título overlay + botón Generar
//     per-slide y dots indicadores. Auto-advance cada 9s (pausa al
//     tocar y al esconderse la pestaña).
//
//   • PresetCard — card genérica de preset usada en TODAS las filas
//     de la home (desktop y mobile). Tiene dos layouts internos según
//     `preset.kind`:
//      - design-modelo/design-auto/catalogo-ia/asistente → carátula
//        grande (3:2 desktop · 4:5 mobile) con título + descripción
//        sueltos debajo de la imagen (sin card chrome).
//      - resto → tile 3:4 con imagen full-bleed + título overlay
//        gradient.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Play, Sparkles } from 'lucide-react';
import type { CollectionV2, PresetV2 } from '../mockData';
import { HoverScrollCarousel } from '../HoverScrollCarousel';

// Cuántos presets se muestran por colección. En desktop entran más
// thumbnails porque hay más espacio horizontal. Si una colección tiene
// menos o igual que el count del device, NO se muestra "Ver más"
// (renderizamos todos los presets y no tiene sentido el link).
const PRESETS_PREVIEW_COUNT_MOBILE = 6;
const PRESETS_PREVIEW_COUNT_DESKTOP = 10;

// iOS (iPhone y iPad) bloquea el autoplay si el <video> no está muteado
// como ATRIBUTO en el momento del play. React solo setea la propiedad
// `muted`, no el atributo, así que en iPad las carátulas no arrancaban.
// Este ref fuerza muted/playsinline como atributo y dispara play() (con
// reintento en `canplay` porque usamos preload=metadata). Se usa en todos
// los <video> de autoplay de la home.
export const setupAutoplayVideo = (el: HTMLVideoElement | null) => {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.setAttribute('muted', '');
  el.setAttribute('playsinline', '');
  const tryPlay = () => {
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  // Pausar el video cuando NO está visible (fuera de pantalla, o su contenedor
  // quedó `display:none` — ej. la home detrás de un panel/sub-modo abierto).
  // Un video display:none igual sigue decodificando frames y gasta GPU/CPU;
  // pausarlo libera ese costo. Vuelve a reproducir al verse de nuevo.
  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) tryPlay();
          else if (!el.paused) el.pause();
        }
      },
      { threshold: 0.01 },
    );
    io.observe(el);
  } else {
    tryPlay();
    el.addEventListener('canplay', tryPlay, { once: true });
  }
};

export interface MobileModeHeroProps {
  cards: PresetV2[];
  onCreate: (preset: PresetV2) => void;
}

export const MobileModeHero: React.FC<MobileModeHeroProps> = ({
  cards,
  onCreate,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // `touching` pausa el auto-advance mientras el dedo está apoyado en
  // el carousel — sin esto el browser pelearía con nuestro `scrollTo`
  // a mitad de un swipe.
  const [touching, setTouching] = useState(false);
  // Contador para re-disparar el effect de auto-advance cuando la
  // pestaña vuelve a estar visible (sin tocar activeIdx).
  const [autoTick, setAutoTick] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth <= 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIdx && idx >= 0 && idx < cards.length) {
      setActiveIdx(idx);
    }
  };

  // Auto-advance: cada 9 segundos pasamos al siguiente slide en loop.
  // El timer se resetea cuando `activeIdx` cambia (sea por la propia
  // automatización o por un swipe manual), así el user siempre tiene
  // ese intervalo completo de "lectura" antes de que vuelva a pasar.
  // Se pausa con el dedo apoyado o cuando la pestaña no está visible.
  useEffect(() => {
    if (cards.length < 2) return;
    if (touching) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const id = window.setTimeout(() => {
      const el = scrollRef.current;
      if (!el || el.clientWidth <= 0) return;
      const next = (activeIdx + 1) % cards.length;
      el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    }, 9000);
    return () => window.clearTimeout(id);
  }, [activeIdx, cards.length, touching, autoTick]);

  // Re-arma el timer cuando la pestaña vuelve a estar visible — sin
  // esto, si el user salió y volvió, el carousel quedaba parado.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (!document.hidden) setAutoTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () =>
      document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Lock direccional del swipe. Con `touch-action: pan-y` (en el style) el
  // browser se reserva SOLO el scroll vertical de la página — que sigue
  // funcionando aunque el dedo arranque sobre los videos — y nos deja el eje
  // X a nosotros. Al primer movimiento significativo fijamos el eje del gesto:
  //   • horizontal → movemos el carousel a mano y `preventDefault()` para
  //     matar cualquier scroll vertical nativo (sin esto aparecía el "drift"
  //     diagonal en Y). Al soltar, snap suave al slide más cercano.
  //   • vertical   → no tocamos nada: la página scrollea nativo y el eje X
  //     queda bloqueado por `pan-y`, así que tampoco hay drift horizontal.
  // (touchmove va como listener NO pasivo vía addEventListener porque el
  //  onTouchMove de React es pasivo y ahí `preventDefault` no tiene efecto.)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let axis: 'x' | 'y' | null = null;
    const THRESH = 6;

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startLeft = el.scrollLeft;
      axis = null;
      setTouching(true);
    };
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < THRESH && Math.abs(dy) < THRESH) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') {
        e.preventDefault();
        el.scrollLeft = startLeft - dx;
      }
    };
    const onEnd = () => {
      setTouching(false);
      if (axis === 'x' && el.clientWidth > 0) {
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
      }
      axis = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  // El hero ocupa todo el ancho de pantalla y empieza desde el tope
  // (sin greeting arriba en mobile). El bottom NO termina abrupto —
  // un overlay con gradient transparente→white sobre la parte
  // inferior del carousel hace que la imagen se diluya en el bg
  // blanco de la página. El botón "Crear" vive dentro de esa zona
  // de degradado, anclado al bottom con `absolute`. `bg-white` en el
  // outer evita una línea de subpixel donde el carousel termina y la
  // página continúa.
  return (
    <div className="relative bg-white">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          // `pan-y`: el browser sólo maneja el scroll vertical de la página
          // (sigue andando aunque el dedo arranque sobre los videos). El eje
          // X lo movemos a mano con lock direccional (ver el effect de touch
          // arriba), así el swipe horizontal no "driftea" en Y. El snap entre
          // slides también lo hacemos por JS, por eso no va `snap-mandatory`
          // acá (pelearía con el drag manual frame a frame).
          touchAction: 'pan-y',
        }}
      >
        {cards.map((card) => {
          // Para `design-modelo`, `design-auto` y `catalogo-ia` usamos un
          // video corto como carátula (mp4 720p comprimido a ~400KB-1.5MB).
          // Para los demás kinds caemos al `imageUrl` estático del preset.
          const videoSrc =
            card.kind === 'design-modelo'
              ? '/images/modo_con_modelo/modo_modelo_caratula_mobile.mp4'
              : card.kind === 'design-auto'
                ? '/images/modo_automatico/modo_auto_caratula_mobile.mp4'
                : card.kind === 'catalogo-ia'
                  ? '/images/modo_catalogo_ia/modo_catalogo_caratula_mobile.mp4'
                  : card.kind === 'anuncio'
                    ? '/images/modo_anuncio/modo_anuncio_caratula_mobile.mp4'
                    : card.kind === 'asistente'
                      ? '/images/modo_asistente/modo_asistente_caratula_mobile.mp4'
                      : null;
          const posterSrc =
            card.kind === 'design-modelo'
              ? '/images/modo_con_modelo/modo_modelo_caratula_mobile.png'
              : card.kind === 'design-auto'
                ? '/images/modo_automatico/modo_auto_caratula_mobile.png'
                : card.kind === 'asistente'
                  ? '/images/modo_asistente/modo_asistente_caratula_mobile.png'
                  : undefined;
          return (
          <div
            key={card.id}
            // aspect-[2/3] (más alto que el viejo 3/4) para que el notch /
            // Dynamic Island del iPhone obscurezca una franja menor del
            // hero. La imagen sigue siendo bg-cover, así que solo se
            // amplía el área visible de la foto.
            className="shrink-0 w-screen snap-start relative aspect-[2/3] bg-neutral-100"
          >
            {videoSrc ? (
              <video
                ref={setupAutoplayVideo}
                src={videoSrc}
                poster={posterSrc}
                autoPlay
                loop
                muted
                // `playsInline` para que iOS no abra el video en player
                // fullscreen apenas el user toca el hero.
                playsInline
                // `preload=metadata` para evitar bajar el video entero
                // hasta que el slide entre en viewport (autoplay arranca
                // la descarga real cuando hace falta).
                preload="metadata"
                aria-label={card.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <img
                src={card.imageUrl}
                alt={card.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            )}
            {/* Gradient fade-to-white por slide — vive ADENTRO del slide
                así pinta sobre la imagen pero queda DEBAJO del título +
                botón (que tienen z-10). Sin esto, el gradient global
                fuera del scroll container terminaba ocultando el botón
                porque el `overflow` del scroll crea su propio stacking
                context y el z-index del botón no escapaba. */}
            <div
              className="absolute inset-x-0 pointer-events-none"
              style={{
                bottom: '-8px',
                height: 'calc(20% + 8px)',
                background:
                  'linear-gradient(to bottom, rgba(255,255,255,0) 0%, #FFFFFF 40%, #FFFFFF 100%)',
              }}
            />
            {/* Título overlay — posicionado al ~76% del hero. `z-10`
                lo levanta arriba del overlay del degradado.
                  • design-auto  → blanco (la carátula tiene fondo
                    oscuro, el texto blanco lee bien).
                  • resto        → brand-black (carátulas claras). */}
            <div
              className="absolute z-10 inset-x-0 px-6"
              style={{ top: '76%' }}
            >
              <h2
                className={`text-[28px] font-semibold leading-tight ${
                  card.kind === 'design-auto' || card.kind === 'anuncio'
                    ? 'text-white drop-shadow-lg'
                    : 'text-brand-black/90'
                }`}
              >
                {card.kind === 'design-auto'
                  ? 'Imagen automática'
                  : card.kind === 'design-modelo'
                    ? 'Imagen personalizada'
                    : card.title}
              </h2>
            </div>
            {/* Botón Generar PER SLIDE — vive dentro de cada slide
                para que se mueva en el carousel cuando el user swipea
                horizontalmente, en vez de quedarse fijo abajo. Cada
                botón abre su propia card del modo. */}
            <div className="absolute z-10 inset-x-0 bottom-3 px-5 pointer-events-none">
              <button
                type="button"
                onClick={() => onCreate(card)}
                className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-brand-black text-white active:scale-[0.99] transition-transform shadow-lg"
              >
                <Sparkles className="w-5 h-5" />
                <span className="text-[15.5px] font-semibold">Generar</span>
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Page indicator — render al final del DOM para que pinte
          arriba sin depender de z-index. Estilos inline para descartar
          problemas de clases Tailwind. Posicionado al ~71% del top,
          justo arriba del título de los modos (que está al 76%). El
          botón Generar ahora vive dentro de cada slide, así que este
          indicador y el degradado son las únicas overlays "globales". */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '71%',
          left: 0,
          paddingLeft: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 30,
          pointerEvents: 'none',
        }}
      >
        {(() => {
          // Los modos `design-auto` y `anuncio` usan carátulas oscuras
          // (modo_auto_caratula / el escenario oscuro del anuncio), así que
          // en esos slides los dots oscuros casi no se distinguen. Cuando el
          // slide activo es uno de esos, los dots se vuelven claros y se les
          // agrega una sombra sutil para que destaquen. En el resto de modos
          // (carátulas claras) vuelven al brand-black.
          const onDarkSlide =
            cards[activeIdx]?.kind === 'design-auto' ||
            cards[activeIdx]?.kind === 'anuncio';
          const activeColor = onDarkSlide ? '#FFFFFF' : '#02062B';
          const inactiveColor = onDarkSlide
            ? 'rgba(255, 255, 255, 0.55)'
            : 'rgba(2, 6, 43, 0.7)';
          const shadow = onDarkSlide
            ? '0 1px 2px rgba(0, 0, 0, 0.25)'
            : 'none';
          return cards.map((_, i) => {
            const active = i === activeIdx;
            return (
              <span
                key={i}
                style={{
                  display: 'block',
                  height: active ? '8px' : '10px',
                  width: active ? '24px' : '10px',
                  borderRadius: '9999px',
                  backgroundColor: active ? activeColor : inactiveColor,
                  boxShadow: shadow,
                  transition: 'all 200ms ease-out',
                }}
              />
            );
          });
        })()}
      </div>
    </div>
  );
};

export interface PresetCardProps {
  preset: PresetV2;
  onClick: () => void;
  /** Modifier opcional (ej: 'hidden md:block' para mostrar solo en desktop). */
  className?: string;
}

export const PresetCard: React.FC<PresetCardProps> = ({
  preset,
  onClick,
  className,
}) => {
  // Cards de los modos "design" (Imagen con modelo + Modo automático +
  // Catálogo IA): layout de card container con imagen en la parte
  // superior, título + descripción debajo. Los 3 modos comparten layout
  // y ancho (`flex-1 sm:basis-0`) para que se repartan el row equitativa-
  // mente en desktop.
  //   • mobile  → 85% del row con la imagen aspect-[3/4]; al ver una
  //               card entera se asoma un pedacito de la siguiente para
  //               invitar al scroll horizontal.
  //   • desktop → flex-1 con la imagen aspect-[4/5] dentro del card; el
  //               alto total queda definido por la imagen + el bloque
  //               de texto.
  if (
    preset.kind === 'design-modelo' ||
    preset.kind === 'design-auto' ||
    preset.kind === 'catalogo-ia' ||
    preset.kind === 'asistente' ||
    preset.kind === 'anuncio'
  ) {
    // Carátula desktop: usamos videos cortos por modo (mp4 comprimido a
    // ~250KB-1.7MB). En mobile esta rama no se renderiza para
    // design-row porque ahí muestra `MobileModeHero` directamente. Para
    // otras filas (templates) que reusan este componente caen al else
    // de abajo con `card.imageUrl`.
    const desktopVideoSrc =
      preset.kind === 'design-modelo'
        ? '/images/modo_con_modelo/modo_modelo_caratula_desktop.mp4'
        : preset.kind === 'design-auto'
          ? '/images/modo_automatico/modo_auto_caratula_desktop.mp4'
          : preset.kind === 'catalogo-ia'
            ? '/images/modo_catalogo_ia/modo_catalogo_caratula_desktop.mp4'
            : preset.kind === 'anuncio'
              ? '/images/modo_anuncio/modo_anuncio_caratula_desktop.mp4'
              : preset.kind === 'asistente'
                ? '/images/modo_asistente/modo_asistente_caratula_desktop.mp4'
                : null;
    return (
      <button
        type="button"
        onClick={onClick}
        // Mobile: 85% del row (carousel manual swipe).
        // Desktop: ancho fijo así el row se vuelve scrolleable horizontal
        // — apuntamos a que entren ~3.5 cards en un viewport de ~1500px
        // de contenido (sidebar incluido). En pantallas más anchas se ven
        // 4+; en más angostas se ven 2.5-3. El `shrink-0` impide que el
        // flex container las achique a menos de su ancho declarado.
        className={`shrink-0 w-[85%] text-left group active:scale-[0.99] transition-transform flex flex-col sm:w-[420px] ${className || ''}`}
      >
        {/* Imagen carátula con las 4 esquinas redondeadas. En desktop
            ratio 3:2 (un poco más alta que 16:9), suficiente para que
            la carátula se vea cuadrada-ish sin ocupar demasiado vertical;
            en mobile mantenemos 4:5 porque cada card ocupa el 85% del
            ancho y una ratio panorámica quedaría muy baja. */}
        <div data-carousel-media className="relative aspect-[4/5] sm:aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
          {desktopVideoSrc ? (
            <video
              ref={setupAutoplayVideo}
              src={desktopVideoSrc}
              poster={preset.imageUrl}
              autoPlay
              loop
              muted
              // `playsInline` para evitar fullscreen player en touch desktops
              // (iPad en modo desktop, etc.).
              playsInline
              preload="metadata"
              aria-label={preset.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          ) : (
            <img
              src={preset.imageUrl}
              alt={preset.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          )}
        </div>
        {/* Texto suelto debajo de la imagen — sin card, sin border, sin
            padding lateral. */}
        <div className="mt-3 flex flex-col gap-0.5">
          <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 group-hover:text-brand-primary">
            {preset.title}
          </h3>
          {preset.description && (
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              {preset.description}
            </p>
          )}
        </div>
      </button>
    );
  }
  const isVideo = preset.outputType === 'video';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 w-[42%] sm:w-[270px] aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.98] transition-transform ${className || ''}`}
    >
      {isVideo ? (
        <video
          ref={setupAutoplayVideo}
          src={preset.imageUrlFull || preset.imageUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={preset.imageUrl}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
        />
      ) : (
        <img
          src={preset.imageUrl}
          alt={preset.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
        />
      )}
      {isVideo && (
        // Badge play: círculo semitransparente arriba a la derecha,
        // indicando que la card es video y no imagen.
        <span className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white shadow">
          <Play className="w-3.5 h-3.5 fill-white" strokeWidth={0} />
        </span>
      )}
      {/* Gradient overlay para que el título sea legible sobre cualquier foto. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
      <p className="absolute bottom-2.5 left-3 right-3 text-left text-white text-[13px] font-medium leading-tight drop-shadow">
        {preset.title}
      </p>
    </button>
  );
};

// ─── Collection row ─────────────────────────────────────────────────────
// Cada colección: título + (subtítulo opcional) + grid horizontal
// scrolleable de preset cards. Si hay más presets que `PRESETS_PREVIEW_COUNT`
// agregamos al final una card de "Ver más" que navega al detalle.
export interface CollectionRowProps {
  collection: CollectionV2;
  onPresetClick: (p: PresetV2) => void;
  onVerMas: () => void;
}

export const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  onPresetClick,
  onVerMas,
}) => {
  // Renderizamos hasta el count DESKTOP, pero las cards más allá del count
  // MOBILE quedan `hidden md:block` — desktop las ve, mobile no.
  // "Ver más" se muestra/oculta por breakpoint según si el respectivo
  // count se excede.
  const total = collection.presets.length;
  const visible = collection.presets.slice(0, PRESETS_PREVIEW_COUNT_DESKTOP);
  const moreOnMobile = total > PRESETS_PREVIEW_COUNT_MOBILE;
  const moreOnDesktop = total > PRESETS_PREVIEW_COUNT_DESKTOP;
  // Si no hay overflow en NINGÚN device, el link/card no se renderiza.
  // Si hay overflow solo en mobile, se renderiza con `md:hidden` (oculto
  // en desktop). Si hay overflow en desktop también, se renderiza sin
  // ese modifier.
  const anyMore = moreOnMobile || moreOnDesktop;
  const verMasResponsive = !moreOnDesktop ? 'md:hidden' : '';

  // La fila virtual "Diseña tu imagen" no muestra header — las cards
  // ya llevan el nombre del modo en el badge centrado, repetir el
  // título arriba era redundante.
  const hideHeader = collection.id === 'design-row';
  return (
    <section>
      {!hideHeader && (
        <header className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[18px] sm:text-[20px] font-semibold text-brand-black/90 leading-tight min-w-0">
            {collection.title}
          </h2>
          {anyMore && (
            <button
              type="button"
              onClick={onVerMas}
              className={`inline-flex items-center gap-0.5 text-[13px] text-brand-primary hover:opacity-75 shrink-0 transition-opacity ${verMasResponsive}`}
            >
              Ver más
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </header>
      )}
      <HoverScrollCarousel
        arrowAlignToMedia
        className="-mx-5 sm:-mx-8 px-5 sm:px-8 flex gap-3 overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {visible.map((preset, i) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            onClick={() => onPresetClick(preset)}
            // Cards más allá del count mobile se ocultan en mobile.
            className={
              i >= PRESETS_PREVIEW_COUNT_MOBILE ? 'hidden md:block' : ''
            }
          />
        ))}
        {anyMore && (
          <button
            type="button"
            onClick={onVerMas}
            className={`shrink-0 w-[42%] sm:w-[270px] aspect-[3/4] rounded-xl border border-dashed border-neutral-300 bg-white hover:border-brand-primary/40 hover:bg-brand-primary/[0.02] transition-colors flex flex-col items-center justify-center gap-2 text-[#3C3C43]/55 hover:text-brand-primary ${verMasResponsive}`}
          >
            <ChevronRight className="w-5 h-5" />
            <span className="text-[13px] font-medium">Ver toda la colección</span>
          </button>
        )}
      </HoverScrollCarousel>
    </section>
  );
};

// ─── Home skeleton ─────────────────────────────────────────────────────
// Se muestra mientras restauramos scroll después de volver desde una
// sub-ruta (categoría, plantillas). Estructura simplificada que imita
// la home: hero arriba + 2-3 rows de cards horizontales debajo. Las
// cards usan `animate-pulse` con fondo `bg-black/[0.06]` — mismo
// tratamiento que el skeleton de `ColeccionDetalle` y otros pages.
export const HomeSkeleton: React.FC = () => {
  const rowCardWidths = ['w-[42%]', 'w-[42%]', 'w-[42%]', 'w-[42%]'];
  return (
    <div className="max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24">
      {/* Hero / greeting */}
      <div className="hidden sm:block max-w-2xl mx-auto mb-12 sm:mb-14 text-center">
        <div className="h-9 sm:h-12 w-3/4 mx-auto rounded-md bg-black/[0.06] animate-pulse" />
        <div className="h-5 w-1/2 mx-auto rounded-md bg-black/[0.06] animate-pulse mt-3" />
      </div>
      {/* Mobile hero: card grande */}
      <div className="sm:hidden -mx-5 aspect-[3/4] bg-black/[0.06] animate-pulse" />
      {/* Desktop design-row: dos cards lado a lado */}
      <div className="hidden sm:flex gap-3 mt-10">
        <div className="flex-1 h-[360px] rounded-xl bg-black/[0.06] animate-pulse" />
        <div className="flex-1 h-[360px] rounded-xl bg-black/[0.06] animate-pulse" />
      </div>
      {/* Desktop CTA */}
      <div className="hidden sm:block mt-10 sm:mt-12">
        <div className="h-[100px] rounded-2xl bg-black/[0.06] animate-pulse" />
      </div>
      {/* Rows de categorías — title chico + 4 cards horizontales */}
      <div className="mt-10 sm:mt-14 space-y-10 sm:space-y-12 sm:hidden">
        {Array.from({ length: 3 }).map((_, ri) => (
          <div key={ri}>
            <div className="h-5 w-32 rounded-md bg-black/[0.06] animate-pulse mb-3" />
            <div className="-mx-5 px-5 flex gap-3 overflow-x-hidden">
              {rowCardWidths.map((w, i) => (
                <div
                  key={i}
                  className={`shrink-0 ${w} aspect-[3/4] rounded-xl bg-black/[0.06] animate-pulse`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
